<template>
  <b-modal
    id="stockMovementReportModal"
    ref="stockMovementReportModal"
    size="xl"
    :title="'ดาวน์โหลดความเคลื่อนไหวสต๊อก'"
    no-close-on-backdrop
    @close="close"
  >
    <b-form-group label-cols="4" label="ข้อมูลช่วงวันที่:" label-align="right">
      <DatePickerField
        v-model="dateRange"
        range
        range-separator=" - "
        :is-required="true"
        :is-error="submitted && !isDateRangeValid"
        :disabled-date="isFutureDate"
        placeholder="เลือกช่วงวันที่ของข้อมูล"
      />
    </b-form-group>

    <b-form-group
      label-cols="4"
      label="รายการสินค้า:"
      label-align="right"
      class="product-scope-group"
    >
      <b-form-radio-group v-model="productScope">
        <b-form-radio value="ALL">ทั้งหมด</b-form-radio>
        <b-form-radio value="SOME">เลือกบางรายการ</b-form-radio>
      </b-form-radio-group>
    </b-form-group>

    <template v-if="productScope === 'SOME'">
      <b-form-group label-cols="4" label="">
        <div class="search-field-wrapper">
          <b-form-input
            v-model="prefixSearchText"
            class="required"
            :class="{ 'is-invalid': submitted && !isProductScopeValid }"
            placeholder="พิมพ์เพื่อค้นหา prefix ของสินค้า"
            autocomplete="off"
            @focus="showPrefixDropdown = true"
            @blur="onPrefixBlur"
          />
          <ul v-if="showPrefixDropdown && prefixMatches.length" class="search-dropdown">
            <li v-for="group in prefixMatches" :key="group.value" @mousedown.prevent="togglePrefixChip(group)">
              <b-form-checkbox :checked="isPrefixSelected(group.value)" tabindex="-1">
                {{ group.value }} {{ group.count }} รายการ
              </b-form-checkbox>
            </li>
          </ul>
        </div>
      </b-form-group>

      <b-form-group label-cols="4" label="">
        <div class="search-field-wrapper">
          <b-form-input
            v-model="itemSearchText"
            class="required"
            placeholder="พิมพ์เพื่อค้นหารายการของสินค้า"
            autocomplete="off"
            @focus="showItemDropdown = true"
            @blur="onItemBlur"
          />
          <ul v-if="showItemDropdown && itemMatches.length" class="search-dropdown">
            <li v-for="product in itemMatches" :key="product.productCode" @mousedown.prevent="toggleItemChip(product)">
              <b-form-checkbox :checked="isItemSelected(product.productCode)" tabindex="-1">
                {{ product.productCode }} {{ product.productNameMain }}
              </b-form-checkbox>
            </li>
          </ul>
        </div>
      </b-form-group>

      <b-form-group v-if="prefixChips.length || itemChips.length" label-cols="4" label="">
        <div class="chip-list">
          <b-badge v-for="chip in prefixChips" :key="'p-' + chip.value" pill variant="light" class="chip">
            {{ chip.value }} {{ chip.count }} รายการ
            <i class="fas fa-times chip-remove" @click="removePrefixChip(chip.value)"></i>
          </b-badge>
          <b-badge v-for="chip in itemChips" :key="'i-' + chip.productCode" pill variant="light" class="chip">
            {{ chip.productCode }} {{ chip.productNameMain }}
            <i class="fas fa-times chip-remove" @click="removeItemChip(chip.productCode)"></i>
          </b-badge>
        </div>
      </b-form-group>
    </template>

    <template #modal-footer>
      <b-button variant="secondary" @click="close">ยกเลิก</b-button>
      <b-button variant="primary" :disabled="isExporting" @click="handleExport">
        <i class="fas fa-file-download"></i>
        {{ isExporting ? "กำลังดาวน์โหลด " + progress + "%" : "ดาวน์โหลด" }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import DatePickerField from "@/components/table/stock/fields/DatePickerField.vue";

export default {
  name: "StockMovementReportModal",
  components: { DatePickerField },
  data() {
    return {
      dateRange: [],
      productScope: "ALL",
      prefixSearchText: "",
      itemSearchText: "",
      showPrefixDropdown: false,
      showItemDropdown: false,
      prefixChips: [],
      itemChips: [],
      submitted: false,
      isExporting: false,
      progress: 0,
    };
  },
  computed: {
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
    }),
    isDateRangeValid() {
      return Array.isArray(this.dateRange) && this.dateRange[0] && this.dateRange[1];
    },
    isProductScopeValid() {
      return this.productScope === "ALL" || this.prefixChips.length > 0 || this.itemChips.length > 0;
    },
    prefixGroupCounts() {
      const counts = new Map();
      (this.getProductList || []).forEach(product => {
        const code = product.productCode || "";
        const match = code.match(/^[A-Za-z]+/);
        const key = (match ? match[0] : code).toUpperCase();
        if (!key) return;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      return counts;
    },
    prefixMatches() {
      const search = this.prefixSearchText.trim().toUpperCase();
      if (!search) return [];
      const selected = new Set(this.prefixChips.map(c => c.value));
      return [...this.prefixGroupCounts.entries()]
        .filter(([value]) => value.startsWith(search) && !selected.has(value))
        .map(([value, count]) => ({ value, count }))
        .slice(0, 20);
    },
    itemMatches() {
      const search = this.itemSearchText.trim().toLowerCase();
      if (!search) return [];
      const selected = new Set(this.itemChips.map(c => c.productCode));
      return (this.getProductList || [])
        .filter(product => {
          if (selected.has(product.productCode)) return false;
          const code = (product.productCode || "").toLowerCase();
          const name = (product.productNameMain || "").toLowerCase();
          return code.includes(search) || name.includes(search);
        })
        .slice(0, 20);
    },
  },
  methods: {
    ...mapActions({
      exportStockMovementReport: "moduleProductInventory/exportStockMovementReport",
    }),
    show() {
      this.dateRange = [];
      this.productScope = "ALL";
      this.prefixSearchText = "";
      this.itemSearchText = "";
      this.prefixChips = [];
      this.itemChips = [];
      this.submitted = false;
      this.$bvModal.show("stockMovementReportModal");
    },
    close() {
      this.$bvModal.hide("stockMovementReportModal");
    },
    isFutureDate(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(date);
      target.setHours(0, 0, 0, 0);
      return target > today;
    },
    onPrefixBlur() {
      setTimeout(() => { this.showPrefixDropdown = false; }, 150);
    },
    onItemBlur() {
      setTimeout(() => { this.showItemDropdown = false; }, 150);
    },
    isPrefixSelected(value) {
      return this.prefixChips.some(c => c.value === value);
    },
    isItemSelected(productCode) {
      return this.itemChips.some(c => c.productCode === productCode);
    },
    togglePrefixChip(group) {
      if (this.isPrefixSelected(group.value)) {
        this.removePrefixChip(group.value);
      } else {
        this.prefixChips.push(group);
      }
    },
    toggleItemChip(product) {
      if (this.isItemSelected(product.productCode)) {
        this.removeItemChip(product.productCode);
      } else {
        this.itemChips.push(product);
      }
    },
    removePrefixChip(value) {
      this.prefixChips = this.prefixChips.filter(c => c.value !== value);
    },
    removeItemChip(productCode) {
      this.itemChips = this.itemChips.filter(c => c.productCode !== productCode);
    },
    async handleExport() {
      this.submitted = true;
      // Invalid fields are flagged inline under each field (all at once), so
      // there is no separate toast for them.
      if (!this.isDateRangeValid || !this.isProductScopeValid) return;
      this.isExporting = true;
      this.progress = 0;
      try {
        const clinicUrl = this.$route.params.clinicUrl;
        const branchUrl = this.$route.params.branchUrl;
        const dateFrom = moment(this.dateRange[0]).format("YYYY-MM-DD");
        const dateTo = moment(this.dateRange[1]).format("YYYY-MM-DD");

        const onProgress = fraction => {
          this.progress = Math.round(fraction * 100);
        };

        const blob = await this.exportStockMovementReport({
          clinicUrl,
          branchUrl,
          dateFrom,
          dateTo,
          codePrefixes: this.prefixChips.length ? this.prefixChips.map(c => c.value).join(",") : undefined,
          itemCodes: this.itemChips.length ? this.itemChips.map(c => c.productCode).join(",") : undefined,
          onProgress,
        });

        const fileName = `Stock_Movement_Report_${branchUrl}_${dateFrom}_${dateTo}.csv`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.close();
      } catch (error) {
        console.error("Export stock movement report error:", error);
        this.$emit("error", "เกิดข้อผิดพลาดในการ export รายงานความเคลื่อนไหวสินค้า");
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .product-scope-group .form-row {
  align-items: center;
}

::v-deep .modal-body {
  max-height: 60vh;
  overflow-y: auto;
}

.search-field-wrapper {
  position: relative;
  margin-bottom: 8px;
}

.search-dropdown {
  position: absolute;
  z-index: 20;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 4px 0;
  list-style: none;
}

.search-dropdown li {
  padding: 4px 12px;
  cursor: pointer;
}

.search-dropdown li:hover {
  background-color: #f5f6f8;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.chip {
  font-weight: normal;
  font-size: 13px;
  padding: 6px 10px;
}

.chip-remove {
  margin-left: 6px;
  cursor: pointer;
}
</style>
